import { useEffect, useState, type FormEvent } from 'react';
import { ArrowDown, ArrowUp, Palette, Plus, Star, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/Modal';
import { api, ApiError } from '@/lib/api';

const BASE_LAYOUTS = ['classic', 'modern', 'minimalist'] as const;
type BaseLayout = (typeof BASE_LAYOUTS)[number];

const SECTION_KEYS = [
  'summary',
  'experience',
  'projects',
  'education',
  'skills',
  'languages',
] as const;
type SectionKey = (typeof SECTION_KEYS)[number];
const SECTION_LABELS: Record<SectionKey, string> = {
  summary: 'Summary',
  experience: 'Experience',
  projects: 'Projects',
  education: 'Education',
  skills: 'Skills',
  languages: 'Languages',
};

/** Mirrors CvTemplateThemeResolver::FONT_STACKS's allowlist keys. */
const FONT_OPTIONS = [
  { value: '', label: "Template's default font" },
  { value: 'inter', label: 'Inter (sans-serif)' },
  { value: 'georgia', label: 'Georgia (serif)' },
  { value: 'roboto', label: 'Roboto (sans-serif)' },
  { value: 'merriweather', label: 'Merriweather (serif)' },
  { value: 'source_sans_pro', label: 'Source Sans Pro (sans-serif)' },
] as const;

const fieldClass =
  'h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface CvTemplateRow {
  id: string;
  name: string;
  slug: string;
  base_layout: BaseLayout;
  accent_color: string | null;
  font_family: string | null;
  section_order: SectionKey[] | null;
  density: 'comfortable' | 'compact';
  is_active: boolean;
  is_default: boolean;
}

/**
 * Admin "curated theme builder" — create/edit/delete the CV templates
 * end users pick from when exporting a CV to PDF/DOCX. See
 * App\Services\Cv\CvTemplateThemeResolver: deleting or deactivating a
 * row is always safe, it just reverts that slug to its built-in look.
 */
export function CvTemplatesPage() {
  const [templates, setTemplates] = useState<CvTemplateRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CvTemplateRow | null>(null);

  function load() {
    api<{ data: CvTemplateRow[] }>('/admin/cv-templates')
      .then((res) => setTemplates(res.data))
      .catch(() => setTemplates([]));
  }
  useEffect(load, []);

  async function toggleActive(t: CvTemplateRow) {
    setBusy(t.id);
    setError(null);
    try {
      await api(`/admin/cv-templates/${t.id}`, {
        method: 'PATCH',
        json: { is_active: !t.is_active },
      });
      load();
    } catch {
      setError('Update failed.');
    } finally {
      setBusy(null);
    }
  }

  async function setDefault(t: CvTemplateRow) {
    setBusy(t.id);
    setError(null);
    try {
      await api(`/admin/cv-templates/${t.id}`, { method: 'PATCH', json: { is_default: true } });
      load();
    } catch {
      setError('Update failed.');
    } finally {
      setBusy(null);
    }
  }

  async function remove(t: CvTemplateRow) {
    if (
      !window.confirm(`Delete "${t.name}"? Existing PDFs already generated with it are unaffected.`)
    ) {
      return;
    }
    setBusy(t.id);
    setError(null);
    try {
      await api(`/admin/cv-templates/${t.id}`, { method: 'DELETE' });
      load();
    } catch {
      setError('Delete failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight">CV templates</h1>
          <p className="text-sm text-muted-foreground">
            Curate the themes candidates can pick when exporting a CV — base layout, accent color,
            font, section order, and density. Deleting or deactivating one is always safe; the three
            built-in layouts keep their default look either way.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New template
        </Button>
      </header>

      {error !== null && (
        <p
          role="alert"
          className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Templates</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {templates === null && <div className="h-24 animate-pulse rounded-2xl bg-muted" />}
          {templates?.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">
              No templates yet — the three built-in layouts (classic/modern/minimalist) still work
              for exports.
            </p>
          )}
          {templates !== null && templates.length > 0 && (
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="py-2 font-bold">Name</th>
                  <th className="py-2 font-bold">Slug</th>
                  <th className="py-2 font-bold">Layout</th>
                  <th className="py-2 font-bold">Accent</th>
                  <th className="py-2 font-bold">Status</th>
                  <th className="py-2 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {templates.map((t) => (
                  <tr key={t.id} className={t.is_active ? '' : 'opacity-70'}>
                    <td className="py-3">
                      <button
                        type="button"
                        className="font-bold underline-offset-2 hover:underline"
                        onClick={() => setEditing(t)}
                      >
                        {t.name}
                      </button>
                    </td>
                    <td className="py-3">
                      <code className="font-mono text-xs">{t.slug}</code>
                    </td>
                    <td className="py-3 capitalize">{t.base_layout}</td>
                    <td className="py-3">
                      {t.accent_color !== null ? (
                        <span className="inline-flex items-center gap-2">
                          <span
                            aria-hidden
                            className="inline-block h-4 w-4 rounded-full border border-border"
                            style={{ backgroundColor: t.accent_color }}
                          />
                          <code className="font-mono text-xs">{t.accent_color}</code>
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">default</span>
                      )}
                    </td>
                    <td className="py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant={t.is_active ? 'default' : 'secondary'}>
                          {t.is_active ? 'active' : 'inactive'}
                        </Badge>
                        {t.is_default && <Badge variant="secondary">default</Badge>}
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        {!t.is_default && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busy !== null}
                            onClick={() => void setDefault(t)}
                          >
                            <Star className="mr-1 h-4 w-4" /> Make default
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy !== null}
                          onClick={() => void toggleActive(t)}
                        >
                          {t.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy !== null}
                          onClick={() => void remove(t)}
                        >
                          <Trash2 className="mr-1 h-4 w-4" /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {createOpen && (
        <TemplateFormModal
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            load();
          }}
        />
      )}
      {editing !== null && (
        <TemplateFormModal
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function TemplateFormModal({
  template,
  onClose,
  onSaved,
}: {
  template?: CvTemplateRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = template !== undefined;
  const [name, setName] = useState(template?.name ?? '');
  const [slug, setSlug] = useState(template?.slug ?? '');
  const [baseLayout, setBaseLayout] = useState<BaseLayout>(template?.base_layout ?? 'classic');
  const [accentColor, setAccentColor] = useState(template?.accent_color ?? '');
  const [fontFamily, setFontFamily] = useState(template?.font_family ?? '');
  const [density, setDensity] = useState<'comfortable' | 'compact'>(
    template?.density ?? 'comfortable',
  );
  const [sectionOrder, setSectionOrder] = useState<SectionKey[]>(
    template?.section_order ?? [...SECTION_KEYS],
  );
  const [isActive, setIsActive] = useState(template?.is_active ?? true);
  const [isDefault, setIsDefault] = useState(template?.is_default ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= sectionOrder.length) return;
    const next = [...sectionOrder];
    const a = next[index];
    const b = next[target];
    if (a === undefined || b === undefined) return;
    next[index] = b;
    next[target] = a;
    setSectionOrder(next);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      name,
      base_layout: baseLayout,
      accent_color: accentColor === '' ? null : accentColor,
      font_family: fontFamily === '' ? null : fontFamily,
      section_order: sectionOrder,
      density,
      is_active: isActive,
      is_default: isDefault,
      ...(isEdit ? {} : { slug }),
    };
    try {
      if (isEdit) {
        await api(`/admin/cv-templates/${template.id}`, { method: 'PATCH', json: payload });
      } else {
        await api('/admin/cv-templates', { method: 'POST', json: payload });
      }
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError ? (firstValidationError(err) ?? err.message) : 'Save failed.',
      );
      setBusy(false);
    }
  }

  return (
    <Modal title={isEdit ? `Edit — ${template.name}` : 'New CV template'} onClose={onClose}>
      <form className="space-y-4" onSubmit={submit}>
        <label className="block space-y-1.5">
          <span className="text-sm font-bold">Name</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-bold">Slug</span>
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            disabled={isEdit}
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
            placeholder="e.g. sunset-modern"
            required={!isEdit}
          />
          {isEdit && <span className="text-xs text-muted-foreground">Slug can't be changed.</span>}
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-bold">Base layout</span>
          <select
            value={baseLayout}
            onChange={(e) => setBaseLayout(e.target.value as BaseLayout)}
            className={fieldClass}
          >
            {BASE_LAYOUTS.map((l) => (
              <option key={l} value={l} className="capitalize">
                {l}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1.5">
            <span className="text-sm font-bold">Accent color</span>
            <div className="flex items-center gap-2">
              <input
                type="color"
                aria-label="Accent color"
                value={accentColor === '' ? '#1c1917' : accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                className="h-10 w-12 rounded-md border border-input bg-background p-1"
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => setAccentColor('')}>
                Use default
              </Button>
            </div>
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-bold">Font</span>
            <select
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              className={fieldClass}
            >
              {FONT_OPTIONS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block space-y-1.5">
          <span className="text-sm font-bold">Density</span>
          <select
            value={density}
            onChange={(e) => setDensity(e.target.value as 'comfortable' | 'compact')}
            className={fieldClass}
          >
            <option value="comfortable">Comfortable</option>
            <option value="compact">Compact</option>
          </select>
        </label>

        <fieldset className="space-y-1.5">
          <legend className="text-sm font-bold">Section order</legend>
          <ul className="divide-y divide-border rounded-md border border-input">
            {sectionOrder.map((key, i) => (
              <li key={key} className="flex items-center justify-between px-3 py-1.5 text-sm">
                <span>{SECTION_LABELS[key]}</span>
                <span className="flex gap-1">
                  <button
                    type="button"
                    aria-label={`Move ${SECTION_LABELS[key]} up`}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${SECTION_LABELS[key]} down`}
                    disabled={i === sectionOrder.length - 1}
                    onClick={() => move(i, 1)}
                    className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </fieldset>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Active
          </label>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            Default template
          </label>
        </div>

        {error !== null && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            <Palette className="mr-2 h-4 w-4" />{' '}
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Pull the first field error out of a Laravel 422 body. */
function firstValidationError(err: ApiError): string | null {
  const body = err.body;
  if (typeof body === 'object' && body !== null && 'errors' in body) {
    const errors = (body as { errors: Record<string, string[]> }).errors;
    const first = Object.values(errors)[0];
    if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
  }
  return null;
}
